import { useQueryClient } from '@tanstack/react-query'
import queryString from 'query-string'
import React from 'react'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  SetTransactionStatusFailureToastMessage,
  SetTransactionStatusSuccessToastMessage,
} from 'src/app/Toast/Toast'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { OrgExpenseReportType } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import {
  OrgExpenseReport,
  OrgExpenseReportCycle,
  OrgExpenseReportCycleGroup,
  OrgExpenseReportCycleModel,
  OrgExpenseReportMetrics,
} from 'src/models/orgExpenseReports.models'
import { PaginationResponse } from 'src/models/pagination.models'
import {
  TransactionFinalApprovalStatus,
  transactionReviewStatusToString,
} from 'src/models/transaction.models'
import {
  ORG_EXPENSE_REPORTS_CYCLES_QK,
  ORG_EXPENSE_REPORTS_QK,
  orgExpenseReportsQueryKeys,
} from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getNextExtendPageParam,
  MutationMethod,
  useExtendInfiniteQuery,
  useExtendMutation,
  UseExtendMutationVariablesBody,
  useExtendQuery,
} from 'src/queries/queries.utils'
import { updateTransactionListResponses } from 'src/queries/transactions.queries'
import { createSetIsSyncTransactionsPopupOpen } from 'src/redux/actions/app.actions'
import { getAsyncTransactionUpdateJobStatus } from 'src/redux/actions/data/transactions.actions'
import { PatchOrgExpenseReportRequest } from 'src/requests/patchOrgExpenseReport.request'
import { PatchOrgExpenseReportCycleRequest } from 'src/requests/patchOrgExpenseReportCycle.request'

export const issuerGroupedRCNOrgExpenseReportCycles = (res: {
  cycles: OrgExpenseReportCycle[]
}): OrgExpenseReportCycleGroup[] => {
  const accountReports: OrgExpenseReportCycleModel[] = res.cycles
    .filter(
      (cycle: OrgExpenseReportCycle) =>
        cycle.reportType === OrgExpenseReportType.Account,
    )
    .map(
      (cycle: OrgExpenseReportCycle) => new OrgExpenseReportCycleModel(cycle),
    )
  // group by issuer and order alphabetically by issuer name and account name
  const sortedIssuers = Array.from(
    new Set(
      Object.values(accountReports).reduce<string[]>(
        (acc, curr) => [...acc, curr.issuerName],
        [],
      ),
    ),
  ).sort()
  const issuerGroups = sortedIssuers.reduce<
    Record<
      string,
      {
        issuerName: string
        rcns: OrgExpenseReportCycleModel[]
      }
    >
  >(
    (acc, curr) => ({
      ...acc,
      [curr]: {
        issuerName: curr,
        rcns: [],
      },
    }),
    {},
  )
  accountReports.forEach((rcnReport) => {
    if (issuerGroups[rcnReport.issuerName] !== undefined) {
      issuerGroups[rcnReport.issuerName].rcns = [
        ...issuerGroups[rcnReport.issuerName].rcns,
        rcnReport,
      ].sort((a, b) => (a.accountName > b.accountName ? 1 : -1))
    }
  })
  return Object.values(issuerGroups)
}

export const issuerSortedRCNOrgExpenseReportCycles = (res: {
  cycles: OrgExpenseReportCycle[]
}): OrgExpenseReportCycleModel[] =>
  issuerGroupedRCNOrgExpenseReportCycles(res)
    .flatMap((issuer) => issuer.rcns)
    .sort((a: OrgExpenseReportCycle, b: OrgExpenseReportCycle): number => {
      return a.accountName > b.accountName
        ? 1
        : b.accountName > a.accountName
          ? -1
          : 0
    })
    .map((cycle) => new OrgExpenseReportCycleModel(cycle))

type QueryOrgExpenseReportCyclesOptions<TData> = ExtendBaseQueryOptions<
  { cycles: OrgExpenseReportCycle[] },
  TData
>

export const useQueryOrgExpenseReportCycles = <
  TData = OrgExpenseReportCycle[],
>({
  select,
  enabled = true,
  ...rest
}: QueryOrgExpenseReportCyclesOptions<TData>) => {
  return useExtendQuery<{ cycles: OrgExpenseReportCycle[] }, TData>({
    queryKey: orgExpenseReportsQueryKeys.cycles,
    queryUrl: () => '/orgexpensereports/cycles',
    enabled: enabled,
    select: select,
    ...rest,
  })
}

interface UsePatchOrgExpenseReportCycleRequest {
  id: string
  body: PatchOrgExpenseReportCycleRequest
}

export const usePatchOrgExpenseReportCycle = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    { cycle: OrgExpenseReportCycle },
    UsePatchOrgExpenseReportCycleRequest
  >(MutationMethod.PATCH, {
    mutationUrl: ({ id }) => `/orgexpensereports/cycles/${id}`,
    onSuccess: (data, variables) => {
      // update detail response
      queryClient.setQueryData<{ cycle: OrgExpenseReportCycle }>(
        [ORG_EXPENSE_REPORTS_QK, ORG_EXPENSE_REPORTS_CYCLES_QK, variables.id],
        data,
      )
      // update list responses
      queryClient.setQueryData<{ cycles: OrgExpenseReportCycle[] }>(
        [ORG_EXPENSE_REPORTS_QK, ORG_EXPENSE_REPORTS_CYCLES_QK],
        (oldData) => {
          return {
            cycles:
              oldData?.cycles.map((cycle) =>
                cycle.id === variables.id ? data.cycle : cycle,
              ) ?? [],
          }
        },
      )
    },
  })
}

interface QueryOrgExpenseReportCycleOptions
  extends ExtendBaseQueryOptions<
    { cycle: OrgExpenseReportCycle },
    OrgExpenseReportCycle
  > {
  cycleId: string | undefined
}

export const useQueryOrgExpenseReportCycle = ({
  cycleId,
  enabled = true,
  ...rest
}: QueryOrgExpenseReportCycleOptions) => {
  return useExtendQuery({
    queryKey: orgExpenseReportsQueryKeys.cycleDetail(cycleId),
    queryUrl: () => `/orgexpensereports/cycles/${cycleId}`,
    select: (data) => data?.cycle,
    enabled: enabled && cycleId !== undefined,
    ...rest,
  })
}

interface OrgExpenseReportCycleReportsResponse {
  reports: OrgExpenseReport[]
  pagination: PaginationResponse
}

interface QueryOrgExpenseReportCycleReports
  extends ExtendBaseInfiniteQueryOptions<
    OrgExpenseReportCycleReportsResponse,
    number,
    OrgExpenseReport[]
  > {
  cycleId: string | undefined
}

export const useQueryOrgExpenseReportCycleReports = ({
  cycleId,
  enabled = true,
  ...rest
}: QueryOrgExpenseReportCycleReports) => {
  return useExtendInfiniteQuery({
    queryKey: orgExpenseReportsQueryKeys.historicalReports(cycleId),
    queryUrl: ({ pageParam }) =>
      `/orgexpensereports/cycles/${cycleId}/reports?${queryString.stringify({ page: pageParam, count: 10 })}`,
    enabled: enabled && cycleId !== undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, __) =>
      getNextExtendPageParam(lastPage.pagination),
    select: (data) => data?.pages.flatMap((page) => page.reports),
    ...rest,
  })
}

interface QueryOrgExpenseReport
  extends ExtendBaseQueryOptions<
    { report: OrgExpenseReport },
    OrgExpenseReport
  > {
  reportId: string | undefined
}

export const useQueryOrgExpenseReport = ({
  reportId,
  enabled = true,
  ...rest
}: QueryOrgExpenseReport) => {
  return useExtendQuery({
    queryKey: orgExpenseReportsQueryKeys.detail(reportId),
    queryUrl: () => `/orgexpensereports/${reportId}`,
    enabled: enabled && reportId !== undefined,
    select: (data) => data.report,
    ...rest,
  })
}

interface UsePatchOrgExpenseReportRequest
  extends UseExtendMutationVariablesBody<PatchOrgExpenseReportRequest> {
  id: string
}

export const usePatchOrgExpenseReport = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    { report: OrgExpenseReport },
    UsePatchOrgExpenseReportRequest
  >(MutationMethod.PATCH, {
    mutationUrl: ({ id }) => `/orgexpensereports/${id}`,
    onSuccess: (data, variables) => {
      queryClient.setQueryData([ORG_EXPENSE_REPORTS_QK, variables.id], data)
    },
  })
}

interface QueryOrgExpenseReportMetrics
  extends ExtendBaseQueryOptions<OrgExpenseReportMetrics> {
  reportId: string | undefined
}

export const useQueryOrgExpenseReportMetrics = ({
  reportId,
  enabled = true,
  ...rest
}: QueryOrgExpenseReportMetrics) => {
  return useExtendQuery({
    queryKey: orgExpenseReportsQueryKeys.metrics(reportId),
    queryUrl: () => `/orgexpensereports/${reportId}/metrics`,
    enabled: enabled && reportId !== undefined,
    ...rest,
  })
}

interface UsePatchTransactionReviewStatusRequest
  extends UseExtendMutationVariablesBody<{
    transactionIds?: string[]
    status: TransactionFinalApprovalStatus
  }> {
  orgExpenseReportId: string
  filters?: TransactionsFiltersBody
}

export type UsePatchTransactionReviewStatusResponse = {
  successes: string[]
  failures: string[]
  jobId?: string
}

export const usePatchTransactionReviewStatus = () => {
  const queryClient = useQueryClient()
  const dispatch = useBackgroundDispatch()
  return useExtendMutation<
    UsePatchTransactionReviewStatusResponse,
    UsePatchTransactionReviewStatusRequest
  >(MutationMethod.PATCH, {
    mutationUrl: ({ orgExpenseReportId, filters }) =>
      `/orgexpensereports/${orgExpenseReportId}/transactionstatus?${queryString.stringify({ ...filters })}`,
    onSuccess: (data, variables) => {
      dispatch(createSetIsSyncTransactionsPopupOpen(false))
      // TODO - can remove sync flow once bulk_select_fast_follows flag is removed
      if (data.jobId !== undefined && data.jobId !== null) {
        // async update
        dispatch(getAsyncTransactionUpdateJobStatus(data.jobId))
      } else {
        // sync update
        updateTransactionListResponses(queryClient, {
          transactionIds: variables.body.transactionIds || [],
          updater: (txn) => ({ ...txn, reviewStatus: variables.body.status }),
        })
        if (data.failures.length === 0) {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <SetTransactionStatusSuccessToastMessage
                  count={data.successes.length}
                  status={transactionReviewStatusToString(
                    variables.body.status,
                  )}
                />
              ),
            }),
          )
        } else if (data.failures.length > 0) {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <SetTransactionStatusFailureToastMessage
                  status={transactionReviewStatusToString(
                    variables.body.status,
                  )}
                />
              ),
              failureColorScheme: true,
              hideImage: true,
            }),
          )
        }
      }
    },
  })
}
